import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  FormHelperText,
  Typography,
  Divider,
  Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { studentService, classService, roleService } from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';

const StudentForm = ({ open, onClose, onSubmit, initialData, title = "Add Student" }) => {
  const boolValue = (value) => value === true || value === 1 || value === '1';

  const [formData, setFormData] = useState({
    // Student details
    full_name: '',
    date_of_joining: '',
    is_active: true,
    roll_number: '',
    class_id: '',
    parent_id: '',
    date_of_birth: '',
    gender: '',
    // Parent details
    parent_full_name: '',
    parent_email: '',
    parent_phone: '',
    parent_address: '',
  });

  const [parentSearchPhone, setParentSearchPhone] = useState('');
  const [parentSearchLoading, setParentSearchLoading] = useState(false);
  const [parentSearchMessage, setParentSearchMessage] = useState('');
  const [parentFound, setParentFound] = useState(false);

  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [classes, setClasses] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch roles, classes, and parents when modal opens
  const fetchData = useCallback(async () => {
    if (!open) return;
    
    setLoading(true);
    setError(null);
    try {
      const [rolesResponse, classesResponse, parentsResponse] = await Promise.all([
        roleService.getAll(),
        classService.getAll(),
        // For now, we'll use a mock parents list since we need to filter by role
        // In a real app, you'd have a specific parents endpoint
        Promise.resolve({ data: [] }) // Placeholder for parents
      ]);
      
      setRoles(rolesResponse.data || rolesResponse);
      setClasses(classesResponse.data || classesResponse);
      setParents(parentsResponse.data || parentsResponse);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch required data');
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-select Student role for new students
  useEffect(() => {
    if (roles.length > 0 && !initialData?.id) {
      const studentRole = roles.find(role => role.role_name?.toLowerCase() === 'student');
      if (studentRole) {
        setFormData(prev => ({
          ...prev,
          role_id: studentRole.id
        }));
      }
    }
  }, [roles, initialData?.id]);

  // Initialize form data when initialData changes or modal opens/closes
  useEffect(() => {
    if (initialData && initialData.id) {
      // Editing existing student
      setFormData({
        // Student details
        full_name: initialData.full_name || '',
        date_of_joining: initialData.admission_date || '',
        is_active: initialData.is_active !== undefined ? boolValue(initialData.is_active) : true,
        roll_number: initialData.roll_number || '',
        class_id: initialData.class_id || '',
        parent_id: initialData.user_id || '',
        date_of_birth: initialData.date_of_birth || '',
        gender: initialData.gender || '',
        govt_id: initialData.govt_id || '',
        govt_id_type: initialData.govt_id_type || '',
        reg_no: initialData.reg_no || '',
        promotion_status: initialData.promotion_status || 'current',
        // Parent details
        parent_full_name: initialData.parent_name || '',
        parent_email: initialData.parent_email || '',
        parent_phone: initialData.parent_phone || '',
        parent_address: initialData.parent_address || '',
      });
    } else {
      // Reset form for new student - always reset when modal opens without initialData
      setFormData({
        // Student details
        full_name: '',
        date_of_joining: '',
        is_active: true,
        roll_number: '',
        class_id: '',
        parent_id: '',
        date_of_birth: '',
        gender: '',
        govt_id: '',
        govt_id_type: '',
        reg_no: '',
        promotion_status: 'current',
        // Parent details
        parent_full_name: '',
        parent_email: '',
        parent_phone: '',
        parent_address: '',
      });
      // Reset parent search state
      setParentSearchPhone('');
      setParentSearchMessage('');
      setParentFound(false);
    }
    setErrors({});
  }, [initialData, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle parent phone number search
  const handleParentPhoneChange = (event) => {
    // Only allow digits, max 10 digits
    const phone = event.target.value.replace(/\D/g, '').slice(0, 10);
    setParentSearchPhone(phone);
    setParentSearchMessage('');
    setParentFound(false);
    
    // Clear parent details when phone changes
    setFormData(prev => ({
      ...prev,
      parent_full_name: '',
      parent_email: '',
      parent_phone: '',
      parent_address: '',
      parent_id: ''
    }));
  };

  // Handle parent phone blur - search when 10 digits and starts with 9 or 6
  const handleParentPhoneBlur = () => {
    if (parentSearchPhone.length === 10 && /^[96]/.test(parentSearchPhone)) {
      searchParent();
    } else if (parentSearchPhone.length > 0 && parentSearchPhone.length < 10) {
      setParentSearchMessage('Phone number must be exactly 10 digits');
    } else if (parentSearchPhone.length === 10 && !/^[96]/.test(parentSearchPhone)) {
      setParentSearchMessage('Phone number must start with 9 or 6');
    }
  };

  // Search for parent by phone number
  const searchParent = useCallback(async () => {
    if (!parentSearchPhone || parentSearchPhone.length !== 10 || !/^[96]/.test(parentSearchPhone)) {
      return;
    }

    setParentSearchLoading(true);
    setParentSearchMessage('');

    try {
      // Search for parent in users table by phone number
      const response = await fetch(`${API_ENDPOINTS.USERS}?phone=${parentSearchPhone}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success && data.data && data.data.length > 0) {
        // Find parent role user
        const parentUser = data.data[0];

        if (parentUser) {
          setFormData(prev => ({
            ...prev,
            parent_id: parentUser.id,
            parent_full_name: parentUser.full_name || '',
            parent_email: parentUser.email || '',
            parent_phone: parentUser.phone || '',
            parent_address: parentUser.address || ''
          }));
          setParentFound(true);
          setParentSearchMessage('Parent found! Details loaded successfully.');
        } else {
          setParentSearchMessage('No parent found with this phone number. Please enter parent details manually.');
          setParentFound(false);
        }
      } else {
        setParentSearchMessage('No parent found with this phone number. Please enter parent details manually.');
        setParentFound(false);
      }
    } catch (error) {
      console.error('Error searching for parent:', error);
      setParentSearchMessage('Error searching for parent. Please try again.');
      setParentFound(false);
    } finally {
      setParentSearchLoading(false);
    }
  }, [parentSearchPhone, roles]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Parent validation - only if not found via search
    if (!parentFound) {
      const parentFullName = formData.parent_full_name ? String(formData.parent_full_name).trim() : '';
      if (!parentFullName) {
        newErrors.parent_full_name = 'Parent full name is required';
      }

      const parentEmail = formData.parent_email ? String(formData.parent_email).trim() : '';
      if (!parentEmail) {
        newErrors.parent_email = 'Parent email is required';
      } else if (!/\S+@\S+\.\S+/.test(parentEmail)) {
        newErrors.parent_email = 'Parent email is invalid';
      }

      const parentPhone = formData.parent_phone ? String(formData.parent_phone).trim() : '';
      if (!parentPhone) {
        newErrors.parent_phone = 'Parent phone number is required';
      } else {
        // Validate phone: must start with 9 or 6, exactly 10 digits
        const phoneRegex = /^[9876]\d{9}$/;
        if (!phoneRegex.test(parentPhone)) {
          newErrors.parent_phone = 'Phone number must start with 9,8,7 or 6 and be exactly 10 digits';
        }
      }

      const parentAddress = formData.parent_address ? String(formData.parent_address).trim() : '';
      if (!parentAddress) {
        newErrors.parent_address = 'Parent address is required';
      }
    } else {
      // If parent found but no parent_id, there's an issue
      if (!formData.parent_id) {
        newErrors.parent_full_name = 'Parent search failed. Please try again or enter details manually.';
      }
    }

    // Student validation
    const fullName = formData.full_name ? String(formData.full_name).trim() : '';
    if (!fullName) {
      newErrors.full_name = 'Student full name is required';
    }

    // No student email/phone required

    if (!formData.date_of_joining) {
      newErrors.date_of_joining = 'Date of joining is required';
    }

    const rollNumber = formData.roll_number ? String(formData.roll_number).trim() : '';
    if (!rollNumber) {
      newErrors.roll_number = 'Roll number is required';
    }

    if (!formData.class_id) {
      newErrors.class_id = 'Class is required';
    }

    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, initialData?.id]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Get student role ID
      const parentRole = roles.find(role => role.role_name?.toLowerCase() === 'parent');

      // Prepare student data for the Students API
      const studentData = {
        // Students table fields only
        full_name: formData.full_name,
        class_id: formData.class_id,
        roll_number: formData.roll_number,
        admission_date: formData.date_of_joining,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender || null,
        govt_id: formData.govt_id || null,
        govt_id_type: formData.govt_id_type || null,
        reg_no: formData.reg_no || null,
        promotion_status: formData.promotion_status || 'current',
        is_active: formData.is_active,
      };

      // Handle parent data based on whether parent was found or not
      if (parentFound && formData.parent_id) {
        // Use existing parent
        studentData.user_id = formData.parent_id;
      } else {
        // Create new parent - send parent data to be created
        studentData.role_id = parentRole?.id;
        studentData.parent_full_name = formData.parent_full_name;
        studentData.parent_email = formData.parent_email;
        studentData.parent_phone = formData.parent_phone;
        studentData.parent_address = formData.parent_address;
      }

      // Include ID for updates
      if (initialData?.id) {
        studentData.id = initialData.id;
      }

      await onSubmit(studentData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  }, [formData, initialData?.id, validateForm, onSubmit, parentFound, roles]);

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      full_name: '',
      date_of_joining: '',
      is_active: true,
      roll_number: '',
      class_id: '',
      parent_id: '',
      date_of_birth: '',
      gender: '',
      govt_id: '',
      govt_id_type: '',
      reg_no: '',
      promotion_status: 'current',
      parent_full_name: '',
      parent_email: '',
      parent_phone: '',
      parent_address: '',
    });
    setParentSearchPhone('');
    setParentSearchMessage('');
    setParentFound(false);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box component="form" sx={{ mt: 1 }}>
            {/* Parent Details Section */}
            <Paper sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                Parent/Guardian Details
              </Typography>
              
              {/* Parent Phone Search */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Search Parent by Phone Number"
                  value={parentSearchPhone}
                  onChange={handleParentPhoneChange}
                  onBlur={handleParentPhoneBlur}
                  placeholder="Enter 10-digit phone number (starts with 9 or 6)"
                  inputProps={{ 
                    maxLength: 10,
                    pattern: '[96]\\d{9}'
                  }}
                  helperText={parentSearchMessage || "Enter parent's phone number (must start with 9 or 6, exactly 10 digits)"}
                  InputProps={{
                    endAdornment: parentSearchLoading ? (
                      <CircularProgress size={20} />
                    ) : null
                  }}
                />
              </Box>

              {/* Search Message */}
              {parentSearchMessage && (
                <Alert 
                  severity={parentFound ? 'success' : 'info'} 
                  sx={{ mb: 2 }}
                >
                  {parentSearchMessage}
                </Alert>
              )}
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {/* Parent Full Name */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Parent Full Name"
                  value={formData.parent_full_name}
                  onChange={handleChange('parent_full_name')}
                  error={!!errors.parent_full_name}
                  helperText={errors.parent_full_name}
                  required={!parentFound}
                  disabled={parentFound}
                />

                {/* Parent Email */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Parent Email"
                  type="email"
                  value={formData.parent_email}
                  onChange={handleChange('parent_email')}
                  error={!!errors.parent_email}
                  helperText={errors.parent_email}
                  required={!parentFound}
                  disabled={parentFound}
                />

                {/* Parent Phone */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Parent Phone"
                  value={formData.parent_phone}
                  onChange={(e) => {
                    // Only allow digits, max 10 digits
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData(prev => ({ ...prev, parent_phone: value }));
                    // Clear error when user starts typing
                    if (errors.parent_phone) {
                      setErrors(prev => ({ ...prev, parent_phone: '' }));
                    }
                  }}
                  error={!!errors.parent_phone}
                  helperText={errors.parent_phone || 'Must start with 9 or 6, exactly 10 digits'}
                  required={!parentFound}
                  disabled={parentFound}
                  inputProps={{
                    maxLength: 10,
                    pattern: '[96]\\d{9}'
                  }}
                />

                {/* Parent Address */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Parent Address"
                  multiline
                  rows={3}
                  value={formData.parent_address}
                  onChange={handleChange('parent_address')}
                  error={!!errors.parent_address}
                  helperText={errors.parent_address}
                  required={!parentFound}
                  disabled={parentFound}
                  sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}
                />
              </Box>
            </Paper>

            <Divider sx={{ my: 2 }} />

            {/* Student Details Section */}
            <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                Student Details
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {/* Student Full Name */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Student Full Name"
                  value={formData.full_name}
                  onChange={handleChange('full_name')}
                  error={!!errors.full_name}
                  helperText={errors.full_name}
                  required
                />

                

                {/* Roll Number */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Roll Number"
                  value={formData.roll_number}
                  onChange={handleChange('roll_number')}
                  error={!!errors.roll_number}
                  helperText={errors.roll_number}
                  required
                />

                {/* Class Selection */}
                <FormControl fullWidth margin="normal" error={!!errors.class_id}>
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={formData.class_id}
                    onChange={handleChange('class_id')}
                    label="Class"
                  >
                    {classes.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {cls.name} - {cls.section}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.class_id && <FormHelperText>{errors.class_id}</FormHelperText>}
                </FormControl>

                {/* Date of Birth */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Date of Birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleChange('date_of_birth')}
                  error={!!errors.date_of_birth}
                  helperText={errors.date_of_birth}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />

                {/* Gender */}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={formData.gender}
                    onChange={handleChange('gender')}
                    label="Gender"
                  >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="M">Male</MenuItem>
                    <MenuItem value="F">Female</MenuItem>
                    <MenuItem value="O">Other</MenuItem>
                  </Select>
                </FormControl>

                {/* Government ID Type */}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Government ID Type</InputLabel>
                  <Select
                    value={formData.govt_id_type}
                    onChange={handleChange('govt_id_type')}
                    label="Government ID Type"
                  >
                    <MenuItem value="">Select ID Type</MenuItem>
                    <MenuItem value="Aadhaar">Aadhaar</MenuItem>
                    <MenuItem value="PAN">PAN</MenuItem>
                    <MenuItem value="Voter ID">Voter ID</MenuItem>
                    <MenuItem value="Driving License">Driving License</MenuItem>
                    <MenuItem value="Passport">Passport</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>

                {/* Government ID */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Government ID"
                  value={formData.govt_id}
                  onChange={handleChange('govt_id')}
                  error={!!errors.govt_id}
                  helperText={errors.govt_id}
                />

                {/* Registration Number */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Registration Number"
                  value={formData.reg_no}
                  onChange={handleChange('reg_no')}
                  error={!!errors.reg_no}
                  helperText={errors.reg_no}
                />

                {/* Promotion Status */}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Promotion Status</InputLabel>
                  <Select
                    value={formData.promotion_status}
                    onChange={handleChange('promotion_status')}
                    label="Promotion Status"
                  >
                    <MenuItem value="current">Current</MenuItem>
                    <MenuItem value="pass">Pass</MenuItem>
                  </Select>
                </FormControl>

                {/* Admission Date */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Admission Date"
                  type="date"
                  value={formData.date_of_joining}
                  onChange={handleChange('date_of_joining')}
                  error={!!errors.date_of_joining}
                  helperText={errors.date_of_joining}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />


                {/* Active Status */}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.is_active ? 1 : 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 1 }))}
                    label="Status"
                  >
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {initialData?.id ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentForm;
